import {ioJsonAction, ioJsonDeleteItemAction, ioJsonFetchAction} from 'Core/store/actions/io';
import {isSuccessful} from 'Core/helpers/io';
import {get} from 'lodash';
import {getArray} from 'Core/helpers/data';
import * as itemFilterDataMap from "./dataMap/filter";
import * as listItemDataMap from "./dataMap/listItem";
import {hideLoading, hideLoadingFunction, showLoading} from 'Core/helpers/loading';
import {actionCreators} from 'Core/store/reducers';
import {reducerStoreKey} from './reducer';

/**
 * Reset all Redux values related to this CustomerGroupImportCustomersList component
 * @return {(function(*): void)|*}
 */
export const resetCustomerGroupImportCustomersListActions = () => dispatch => {
	dispatch(actionCreators[reducerStoreKey].reset());
}

/**
 * Fetch the list of customers of a specific customer group
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} customerGroupId - DB ID of the customer group.
 * @param {Object} [filter] - Fetch filter.
 * @param {number} [pageNo] - Number of the page to load (pagination). Starts from 1.
 * @param {number} [perPage] - Number of items per page to load (pagination). Used system default if not specified.
 * @param {string} [sortBy] - Sort field name. The API defines sort fields.
 * @param {SortOrder} [sortDir] - Sort direction.
 * @return {function(*=): Promise<IoJsonFetchResponseObject>}
 */
export const fetchCustomerGroupCustomerListAction = (
	abortCallback, customerGroupId, filter = null, pageNo = 1, perPage, sortBy, sortDir
) => dispatch => {
	return ioJsonFetchAction(
		abortCallback,
		'defaultAuthorizedApi',
		'customer-group/search-customer-group-customers',
		'',
		filter,
		null,
		pageNo,
		perPage,
		sortBy,
		sortDir,
		{customerGroupId}
	)(dispatch)
		// Get mapped data from response data
		.then(responseData => {
			if (isSuccessful(responseData)) {
				return ({
					...responseData,
					filter: itemFilterDataMap.input(get(responseData, 'filter')),
					data: getArray(responseData, 'data').map(i => listItemDataMap.input(i))
				});
			}
			return undefined;
		});
};

/**
 * Load the list of customers of a specific customer group into Redux store
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} customerGroupId - DB ID of the customer group.
 * @param {Object} [filter] - Fetch filter.
 * @param {number} [pageNo] - Number of the page to load (pagination). Starts from 1.
 * @param {number} [perPage] - Number of items per page to load (pagination). Used system default if not specified.
 * @param {string} [sortBy] - Sort field name. The API defines sort fields.
 * @param {SortOrder} [sortDir] - Sort direction.
 * @param {boolean} [loadingOverlay=true] - Flag that specifies if loading overlay is rendered.
 * @return {function(*=): Promise<IoJsonFetchResponseObject>}
 */
export const loadCustomerGroupCustomerListAction = (
	abortCallback, customerGroupId, filter = null, pageNo = 1, perPage, sortBy, sortDir, loadingOverlay = true
) => dispatch => {
	const loading = (loadingOverlay === true ? showLoading("#main-tab-table") : null);
	return fetchCustomerGroupCustomerListAction(
		abortCallback, customerGroupId, filter, pageNo, perPage, sortBy, sortDir
	)(dispatch)
		// Load data into Redux store
		.then(responseData => {
			if (responseData) dispatch(actionCreators[reducerStoreKey].setCustomersListData(responseData));
			if (loading !== null) hideLoading(loading);
			return responseData;
		});
};

/**
 * Clear customers list from Redux store
 *
 * @return {(function(*): void)|*}
 */
export const clearCustomerGroupCustomerListAction = () => dispatch => {
	dispatch(actionCreators[reducerStoreKey].clearCustomersListData());
}

/**
 * Delete a single customer from the specified customer group
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} id - ID of the customer group customer list item.
 * @param {string} customerGroupId - DB ID of the customer group.
 * @param {string} clientId - DB ID of the client (used by super admin).
 * @return {function(*): *}
 */
export const deleteCustomerGroupCustomerListItemAction = (abortCallback, id, customerGroupId, clientId) => dispatch => {
	const loading = showLoading('#customer-group-customer-item-delete-dialog .dialog-content-component .buttons');
	return ioJsonDeleteItemAction(
		// @note abortCallback is set to undefined because delete actions should not be cancelable.
		undefined,
		'defaultAuthorizedApi',
		'customer-group/remove-customers-from-customer-group',
		id,
		{customerGroupId, clientId},
		hideLoadingFunction(loading)
	)(dispatch);
};

/**
 * Delete multiple customers from the specified customer group
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} ids - IDs of the customer group customer list items.
 * @param {string} customerGroupId - DB ID of the customer group.
 * @param {string} clientId - DB ID of the client (used by super admin).
 * @return {function(*): *}
 */
export const deleteCustomerGroupCustomerListItemsAction = (
	abortCallback, ids, customerGroupId, clientId
) => dispatch => {
	const loading = showLoading('#customer-group-customer-items-delete-dialog .dialog-content-component .buttons');
	return ioJsonDeleteItemAction(
		// @note abortCallback is set to undefined because delete actions should not be cancelable.
		undefined,
		'defaultAuthorizedApi',
		'customer-group/remove-customers-from-customer-group',
		ids,
		{customerGroupId, clientId},
		hideLoadingFunction(loading)
	)(dispatch);
};

/**
 * Fetch customer custom fields
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} [loadingOverlaySelector] - Selector for the loading overlay.
 * @param {?string} [clientId=null] - DB ID of the client (used by super admin).
 * @return {function(*): Promise<
 * 	{id: string, name: string, code: string, description: string, type: CustomerCustomFiledType}[]|undefined
 * >}
 */
export const fetchCustomerCustomFieldsAction = (abortCallback, loadingOverlaySelector, clientId = null) => dispatch => {
	const loading = (loadingOverlaySelector ? showLoading(loadingOverlaySelector) : undefined);
	return ioJsonAction(
		abortCallback,
		'defaultAuthorizedApi',
		'customer/fetch-customer-custom-fields',
		{clientId},
		loading ? hideLoadingFunction(loading) : undefined,
	)(dispatch)
		// Get customer custom fields from response data
		.then(res => (res ? getArray(res, 'data') : undefined));
}